import { API_BASE_URL as API_BASE } from '../config';

// Hàm hash đơn giản để tạo Idempotency Key từ request
const generateIdempotencyKey = (url, body) => {
  const str = `${url}_${JSON.stringify(body || {})}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return `key_${Math.abs(hash)}_${localStorage.getItem('token')?.slice(-10)}`;
};

const handleResponse = async (response) => {
  // Xử lý lỗi 401 Unauthorized toàn cục
  if (response.status === 401) {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login?expired=true';
    }
  }

  // Xử lý lỗi 409 Conflict (Spam click)
  if (response.status === 409) {
     const data = await response.json();
     throw new Error(data.message || "Yêu cầu đang được xử lý, vui lòng không gửi liên tiếp.");
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || data.Message || `HTTP error! status: ${response.status}`);
    }
    return data;
  }

  const text = await response.text();
  if (!response.ok) {
    const stripped = text.replace(/<[^>]+>/g, '').trim();
    const message = stripped ? stripped : `HTTP error! status: ${response.status}`;
    throw new Error(message);
  }

  return { success: true, data: text };
};

const getAuthHeaders = (isFormData = false) => {
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`
  };
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const apiClient = {
  get: async (url, params = {}) => {
    const cleanParams = Object.keys(params).reduce((acc, key) => {
      const val = params[key];
      if (val !== null && val !== undefined && val !== "") {
        acc[key] = val;
      }
      return acc;
    }, {});
    const queryString = new URLSearchParams(cleanParams).toString();
    const fullUrl = `${API_BASE}${url}${queryString ? `?${queryString}` : ''}`;
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: getAuthHeaders()
    });
    return handleResponse(response);
  },

  post: async (url, body = {}, options = {}) => {
    const isFormData = body instanceof FormData;
    const idempotencyKey = isFormData ? `fd_${Date.now()}` : generateIdempotencyKey(url, body);
    
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: { 
        ...getAuthHeaders(isFormData), 
        'X-Idempotency-Key': idempotencyKey,
        ...options.headers 
      },
      body: isFormData ? body : JSON.stringify(body)
    });
    return handleResponse(response);
  },

  put: async (url, body = {}, options = {}) => {
    const isFormData = body instanceof FormData;
    const idempotencyKey = isFormData ? `fd_${Date.now()}` : generateIdempotencyKey(url, body);

    const response = await fetch(`${API_BASE}${url}`, {
      method: 'PUT',
      headers: { 
        ...getAuthHeaders(isFormData), 
        'X-Idempotency-Key': idempotencyKey,
        ...options.headers 
      },
      body: isFormData ? body : JSON.stringify(body)
    });
    return handleResponse(response);
  },

  patch: async (url, body = {}, options = {}) => {
    const isFormData = body instanceof FormData;
    const idempotencyKey = isFormData ? `fd_${Date.now()}` : generateIdempotencyKey(url, body);

    const response = await fetch(`${API_BASE}${url}`, {
      method: 'PATCH',
      headers: { 
        ...getAuthHeaders(isFormData), 
        'X-Idempotency-Key': idempotencyKey,
        ...options.headers 
      },
      body: isFormData ? body : JSON.stringify(body)
    });
    return handleResponse(response);
  },

  delete: async (url) => {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        'X-Idempotency-Key': `del_${url.replace(/\//g, '_')}`
      }
    });
    return handleResponse(response);
  },

  upload: async (url, formData) => {
    return apiClient.post(url, formData);
  },

  download: async (url, body = {}) => {
    const response = await fetch(`${API_BASE}${url}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body)
    });
    if (!response.ok) {
        return handleResponse(response);
    }
    return await response.blob();
  }
};

export default apiClient;
